import { useEffect, useState, useRef } from 'react';
import { getPortalDocuments, type PortalDocument } from '@lib/api/portal';
import { PullToRefresh } from '@/components/common';
import { DocumentViewerModal } from '@/components/common/DocumentViewerModal';

const DOC_ICON: Record<string, string> = {
  invoice: 'receipt_long',
  quote: 'request_quote',
  work_order: 'handshake',
  contract: 'gavel',
  change_order: 'change_circle',
  photo: 'image',
  pdf: 'picture_as_pdf',
};

const EXT_ICON: Record<string, string> = {
  pdf: 'picture_as_pdf',
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
  doc: 'description',
  docx: 'description',
  xls: 'table_chart',
  xlsx: 'table_chart',
};

const getIcon = (doc: PortalDocument) => {
  if (doc.document_type && DOC_ICON[doc.document_type]) return DOC_ICON[doc.document_type];
  const ext = (doc.file_name || '').split('.').pop()?.toLowerCase() || '';
  return EXT_ICON[ext] || 'description';
};

// Upload moment — date AND time (timestamps arrive as full ISO strings)
const fmtDate = (d: string) => {
  const dt = d.length > 10 ? new Date(d) : new Date(d + 'T12:00:00');
  if (isNaN(dt.getTime())) return d;
  return d.length > 10
    ? dt.toLocaleString('en-CA', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    : dt.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
};

const DOC_TYPE_LABEL: Record<string, string> = {
  invoice: 'Invoice',
  quote: 'Quote',
  work_order: 'Work Order',
  contract: 'Contract',
  change_order: 'Change Order',
};

// #11 – quick photo upload CTA
interface PhotoUploadProps {
  onUpload: (file: File) => Promise<void>;
}

const PhotoUploadButton = ({ onUpload }: PhotoUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await onUpload(file);
      setDone(true);
      setTimeout(() => setDone(false), 3000);
    } catch {
      alert('Photo upload failed — check your connection and try again.');
    }
    finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <label className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border cursor-pointer transition-colors text-sm font-semibold ${done ? 'bg-green-50 border-green-300 text-green-700' : 'bg-white border-[#C77D3F] text-[#C77D3F] hover:bg-[#C77D3F]/5'}`}>
      <span className="material-symbols-outlined text-base">{done ? 'check_circle' : 'photo_camera'}</span>
      {uploading ? 'Uploading…' : done ? 'Uploaded!' : 'Upload Job Photo'}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        disabled={uploading}
        onChange={handleChange}
      />
    </label>
  );
};

export const EmployeeDocumentsPage = () => {
  const [docs, setDocs] = useState<PortalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadMsg, setUploadMsg] = useState('');
  // Popup viewer (same as the office /jobs documents): images, PDFs, 3D models…
  const [viewerDoc, setViewerDoc] = useState<{ id: number; name: string; type: string; url: string } | null>(null);
  const openViewer = (doc: PortalDocument) => {
    if (!doc.file_path) return;
    setViewerDoc({
      id: doc.id,
      name: doc.label || doc.file_name || doc.title || 'Document',
      type: doc.file_type || '',
      url: `/${String(doc.file_path).replace(/^\//, '')}`,
    });
  };
  const downloadDoc = (d: { url: string; name: string }) => {
    const a = document.createElement('a');
    a.href = d.url;
    a.download = d.name;
    a.click();
  };
  // Folder tree: which job folders are open (all open by default once loaded)
  const [openFolders, setOpenFolders] = useState<Set<number>>(new Set());
  const toggleFolder = (jobId: number) =>
    setOpenFolders(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId); else next.add(jobId);
      return next;
    });

  const load = () => {
    setLoading(true);
    setError(null);
    getPortalDocuments()
      .then(d => {
        setDocs(d);
        // Open every job folder on first load so nothing is hidden
        setOpenFolders(new Set(d.map(x => x.job_id)));
      })
      .catch(() => setError('Failed to load documents. Please try again.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // #11 – photo upload handler (POST to /portal/me/documents/photo — needs backend endpoint)
  const uploadPhoto = async (file: File) => {
    const form = new FormData();
    form.append('photo', file);
    const token = localStorage.getItem('accessToken');
    const res = await fetch('/api/portal/me/documents/photo', {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (!res.ok) throw new Error('Upload failed');
    setUploadMsg('Photo uploaded.');
    setTimeout(() => setUploadMsg(''), 4000);
    load();
  };

  if (loading) return (
    <div className="space-y-3">
      {[...Array(3)].map((_, i) => <div key={i} className="h-20 animate-pulse rounded-xl bg-gray-100" />)}
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center space-y-3">
      <span className="material-symbols-outlined text-red-400 text-4xl">error</span>
      <p className="text-sm text-red-700">{error}</p>
      <button onClick={load} className="text-sm font-semibold text-red-600 border border-red-300 rounded-lg px-4 py-1.5 hover:bg-red-100">
        Retry
      </button>
    </div>
  );

  return (
    <PullToRefresh onRefresh={load}>
      <div className="space-y-4">
        <div>
          <h1 className="text-xl font-bold text-[#2C1810]">My Documents</h1>
          <p className="text-sm text-gray-500 mt-0.5">Documents from your assigned jobs.</p>
        </div>

        {/* BC Building Code — reference viewer */}
        <a href="/portal/employee/bc-code"
          className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 active:bg-gray-50">
          <div className="w-10 h-10 rounded-lg bg-[#C77D3F]/10 flex items-center justify-center flex-shrink-0">
            <span className="material-symbols-outlined text-[#C77D3F] text-xl">menu_book</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-sm">BC Building Code</p>
            <p className="text-xs text-gray-400">Quick reference & full code viewer</p>
          </div>
          <span className="material-symbols-outlined text-gray-300">chevron_right</span>
        </a>

        {/* Safety & WorkSafeBC reference */}
        <a href="/portal/employee/safety"
          className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 active:bg-gray-50">
          <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
            <span className="material-symbols-outlined text-red-600 text-xl">health_and_safety</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-sm">Safety & WorkSafeBC</p>
            <p className="text-xs text-gray-400">Fall protection, excavations, tools, your rights</p>
          </div>
          <span className="material-symbols-outlined text-gray-300">chevron_right</span>
        </a>

        {/* #11 – quick photo upload CTA */}
        <PhotoUploadButton onUpload={uploadPhoto} />
        {uploadMsg && (
          <p className="text-sm text-green-700 flex items-center gap-1">
            <span className="material-symbols-outlined text-sm">check_circle</span>{uploadMsg}
          </p>
        )}

        {docs.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
            <span className="material-symbols-outlined text-gray-300 text-5xl">folder_open</span>
            <p className="text-sm font-medium text-gray-500 mt-3">No documents yet</p>
            <p className="text-xs text-gray-400 mt-1">Documents from your job sites will appear here.</p>
          </div>
        ) : (
          /* Tree view: each job is a folder, its documents are the files inside */
          <div className="space-y-2">
            {(() => {
              const folders = new Map<number, { title: string; files: PortalDocument[] }>();
              for (const doc of docs) {
                const f = folders.get(doc.job_id) || { title: doc.job_title || 'Other documents', files: [] };
                f.files.push(doc);
                folders.set(doc.job_id, f);
              }
              return [...folders.entries()].map(([jobId, folder]) => {
                const open = openFolders.has(jobId);
                return (
                  <div key={jobId} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    {/* Folder row */}
                    <button
                      onClick={() => toggleFolder(jobId)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left active:bg-gray-50"
                    >
                      <span className="material-symbols-outlined text-[#C77D3F] text-2xl">
                        {open ? 'folder_open' : 'folder'}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-semibold text-gray-900 text-sm truncate">{folder.title}</span>
                        <span className="block text-xs text-gray-400">{folder.files.length} file{folder.files.length !== 1 ? 's' : ''}</span>
                      </span>
                      <span className={`material-symbols-outlined text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}>expand_more</span>
                    </button>

                    {/* Files */}
                    {open && (
                      <div className="border-t border-gray-100">
                        {folder.files.map(doc => {
                          const icon = getIcon(doc);
                          const typeLabel = doc.document_type ? (DOC_TYPE_LABEL[doc.document_type] || doc.document_type.replace(/_/g, ' ')) : null;
                          const href = doc.file_path ? `/${String(doc.file_path).replace(/^\//, '')}` : undefined;
                          const inner = (
                            <>
                              {/* Tree indent + connector */}
                              <span className="w-6 flex-shrink-0 flex justify-center text-gray-300 select-none">└</span>
                              <div className="w-8 h-8 rounded-lg bg-[#C77D3F]/10 flex items-center justify-center flex-shrink-0">
                                <span className="material-symbols-outlined text-[#C77D3F] text-base">{icon}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 text-sm truncate">{doc.label || doc.file_name || doc.title || 'Document'}</p>
                                {doc.label && doc.file_name && (
                                  <p className="text-[10px] text-gray-400 truncate">{doc.file_name}</p>
                                )}
                                <div className="flex items-center gap-2 mt-0.5">
                                  {typeLabel && <span className="text-xs text-gray-400 capitalize">{typeLabel}</span>}
                                  <span className="text-xs text-gray-400">{fmtDate(doc.created_at)}</span>
                                </div>
                              </div>
                              {doc.status && (
                                <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-medium capitalize ${
                                  doc.status === 'signed' ? 'bg-green-100 text-green-700' :
                                  doc.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {doc.status}
                                </span>
                              )}
                              {href && <span className="material-symbols-outlined text-gray-300 text-base flex-shrink-0">visibility</span>}
                            </>
                          );
                          return href ? (
                            <button
                              key={doc.id}
                              type="button"
                              onClick={() => openViewer(doc)}
                              className="w-full text-left flex items-center gap-2 px-3 py-2.5 border-b border-gray-50 last:border-b-0 active:bg-gray-50"
                            >
                              {inner}
                            </button>
                          ) : (
                            <div key={doc.id} className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-50 last:border-b-0">
                              {inner}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        )}
      </div>
      <DocumentViewerModal doc={viewerDoc} onClose={() => setViewerDoc(null)} onDownload={downloadDoc} />
    </PullToRefresh>
  );
};
